import { useEffect, useState, type FormEvent } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import DashboardShell from './DashboardShell';
import EmailPreview from './EmailPreview';
import EmailLog from './EmailLog';
import { supabase } from '../../lib/supabase';
import type { Booking, Offer, UserProfile, Vendor, VendorApplication, VendorRequest, VendorStatus } from '../../lib/database.types';
import { formatDate, formatMoney, formatTime, relativeDate } from '../../lib/format';

const tabs = [
  { label: 'Overview', href: '/dashboard' },
  { label: 'Applications', href: '/dashboard/applications' },
  { label: 'Location requests', href: '/dashboard/requests' },
  { label: 'Vendors', href: '/dashboard/vendors' },
  { label: 'Bookings', href: '/dashboard/bookings' },
  { label: 'Members', href: '/dashboard/members' },
  { label: 'Emails', href: '/dashboard/emails' },
];

export default function PlatformDashboard() {
  return (
    <DashboardShell role="platform" tabs={tabs}>
      <Routes>
        <Route index element={<Overview />} />
        <Route path="applications" element={<Applications />} />
        <Route path="requests" element={<Requests />} />
        <Route path="vendors" element={<Vendors />} />
        <Route path="vendors/:id" element={<VendorEdit />} />
        <Route path="bookings" element={<Bookings />} />
        <Route path="members" element={<Members />} />
        <Route path="emails" element={<EmailLog />} />
        <Route path="emails/templates" element={<EmailPreview />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </DashboardShell>
  );
}

function Overview() {
  const [stats, setStats] = useState<{ vendors: number; active: number; bookings: number; users: number; offers: number; apps: number; appsNew: number; requests: number } | null>(null);

  useEffect(() => {
    (async () => {
      const [v, va, b, u, o, a, an, r] = await Promise.all([
        supabase.from('ga_vendors').select('*', { count: 'exact', head: true }),
        supabase.from('ga_vendors').select('*', { count: 'exact', head: true }).eq('status', 'active'),
        supabase.from('ga_bookings').select('*', { count: 'exact', head: true }),
        supabase.from('ga_user_profiles').select('*', { count: 'exact', head: true }),
        supabase.from('ga_offers').select('*', { count: 'exact', head: true }),
        supabase.from('ga_vendor_applications').select('*', { count: 'exact', head: true }),
        supabase.from('ga_vendor_applications').select('*', { count: 'exact', head: true }).eq('status', 'new'),
        supabase.from('ga_vendor_requests').select('*', { count: 'exact', head: true }).eq('status', 'new'),
      ]);
      setStats({
        vendors: v.count ?? 0,
        active: va.count ?? 0,
        bookings: b.count ?? 0,
        users: u.count ?? 0,
        offers: o.count ?? 0,
        apps: a.count ?? 0,
        appsNew: an.count ?? 0,
        requests: r.count ?? 0,
      });
    })();
  }, []);

  return (
    <div>
      <SectionHeader eyebrow="Platform admin" title="Overview" />
      {!stats ? (
        <p className="muted">Loading…</p>
      ) : (
        <div className="r-grid-4">
          <Stat n={stats.appsNew} l="New applications" highlight={stats.appsNew > 0} />
          <Stat n={stats.requests} l="Location requests" highlight={stats.requests > 0} />
          <Stat n={stats.active} l="Active vendors" />
          <Stat n={stats.vendors} l="Total vendors" />
          <Stat n={stats.offers} l="Offers" />
          <Stat n={stats.bookings} l="Bookings" />
          <Stat n={stats.users} l="Members" />
          <Stat n={stats.apps} l="Total applications" />
        </div>
      )}
    </div>
  );
}

function Stat({ n, l, highlight }: { n: number; l: string; highlight?: boolean }) {
  return (
    <div style={{ padding: 24, borderRadius: 16, background: highlight ? 'var(--primary)' : 'var(--card)' }}>
      <div className="display" style={{ fontSize: 48, letterSpacing: '-0.04em' }}>{n}</div>
      <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)' }}>{l}</div>
    </div>
  );
}

function Applications() {
  const [apps, setApps] = useState<VendorApplication[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    const { data } = await supabase
      .from('ga_vendor_applications')
      .select('*')
      .order('created_at', { ascending: false });
    setApps((data as VendorApplication[]) ?? []);
  }
  useEffect(() => { load(); }, []);

  async function approve(id: string) {
    setBusyId(id);
    setError(null);
    const { error } = await supabase.rpc('ga_approve_vendor_application', { p_application_id: id });
    if (error) {
      setError(error.message);
      setBusyId(null);
      return;
    }
    try {
      await supabase.functions.invoke('ga-notify', { body: { kind: 'vendor_approved', application_id: id } });
    } catch { /* non-fatal */ }
    await load();
    setBusyId(null);
  }
  async function reject(id: string) {
    setBusyId(id);
    await supabase.from('ga_vendor_applications').update({ status: 'rejected' }).eq('id', id);
    await load();
    setBusyId(null);
  }

  return (
    <div>
      <SectionHeader eyebrow={`${apps.length} applications`} title="Vendor applications" />
      {error && <div className="field-error" style={{ marginBottom: 16 }}>{error}</div>}
      {apps.length === 0 ? (
        <p className="muted">No applications yet.</p>
      ) : (
        <div className="stack">
          {apps.map((a) => (
            <AppCard key={a.id} a={a} onApprove={() => approve(a.id)} onReject={() => reject(a.id)} busy={busyId === a.id} />
          ))}
        </div>
      )}
    </div>
  );
}

function AppCard({ a, onApprove, onReject, busy }: { a: VendorApplication; onApprove: () => void; onReject: () => void; busy: boolean }) {
  return (
    <div style={{ padding: 24, borderRadius: 18, background: 'var(--card)' }}>
      <div className="row-between" style={{ alignItems: 'start' }}>
        <div style={{ flex: 1 }}>
          <div className="row" style={{ gap: 12, marginBottom: 8 }}>
            <div className="display" style={{ fontSize: 26 }}>{a.bar_name}</div>
            <span className={`tag ${a.status === 'new' ? 'tag-pending' : a.status === 'approved' ? 'tag-active' : 'tag-cancelled'}`}>{a.status}</span>
          </div>
          <div style={{ fontSize: 14, color: 'var(--ink-soft)', marginBottom: 4 }}>
            {a.address} · {a.neighborhood ?? 'No neighborhood'}
          </div>
          <div style={{ fontSize: 14, color: 'var(--ink-soft)' }}>
            {a.contact_name} · {a.contact_email}{a.phone ? ` · ${a.phone}` : ''}
          </div>
          {a.description && (
            <div style={{ marginTop: 12, fontSize: 14, padding: '8px 12px', background: 'var(--bg)', borderRadius: 8 }}>{a.description}</div>
          )}

          {a.offer_title && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--line)' }}>
              <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginBottom: 8 }}>{a.offer_title}</div>
              <div style={{ fontSize: 13, color: 'var(--ink-soft)', marginBottom: 8 }}>
                {a.headcount_min}–{a.headcount_max} guests · {(a.days_of_week ?? []).join(', ')} · {a.start_time?.slice(0, 5)}–{a.end_time?.slice(0, 5)}
              </div>

              {(a.packages?.length ?? 0) > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${a.packages.length}, 1fr)`, gap: 8, marginTop: 8 }}>
                  {a.packages.map((pkg, i) => (
                    <div key={i} style={{ padding: 12, background: 'var(--bg)', borderRadius: 10 }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>{pkg.tier?.[0].toUpperCase() + pkg.tier?.slice(1)} · {formatMoney(pkg.price_cents)}/head</div>
                      <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
                        {pkg.drinks_per_guest} drinks · {pkg.drinks_included.slice(0, 3).join(', ')}{pkg.drinks_included.length > 3 ? '…' : ''}
                      </div>
                      {pkg.food_included && (
                        <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
                          {pkg.food_appetizers_count} apps per {pkg.food_appetizers_per_guests} ppl
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <ul style={{ paddingLeft: 18, color: 'var(--ink-soft)', fontSize: 14, marginTop: 6 }}>
                  <li>Legacy single-package format</li>
                  {a.price_cents != null && <li>{formatMoney(a.price_cents)}/head</li>}
                  <li>{a.drinks_per_guest ?? '?'} drinks per guest</li>
                </ul>
              )}

              {(a.photo_urls ?? []).length > 0 && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  {(a.photo_urls ?? []).map((url, i) => (
                    <div key={i} style={{ width: 80, height: 80, borderRadius: 8, background: `url(${url}) center/cover`, border: '1px solid var(--line)' }} />
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="mono" style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginTop: 12 }}>
            {relativeDate(a.created_at)}
          </div>
        </div>

        {a.status === 'new' && (
          <div className="stack-sm" style={{ minWidth: 160 }}>
            <button onClick={onApprove} disabled={busy} className="btn btn-primary btn-sm" style={{ justifyContent: 'center' }}>
              {busy ? '…' : 'Approve & go live'}
            </button>
            <button onClick={onReject} disabled={busy} className="btn btn-ghost btn-sm" style={{ justifyContent: 'center' }}>
              Reject
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Requests() {
  const [reqs, setReqs] = useState<(VendorRequest & { vendor?: Vendor | null })[]>([]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('ga_vendor_requests')
        .select('*')
        .order('created_at', { ascending: false });
      if (!data) return;
      const vendorIds = (data as VendorRequest[]).map((r) => r.vendor_id).filter(Boolean) as string[];
      let vmap = new Map<string, Vendor>();
      if (vendorIds.length > 0) {
        const { data: vendors } = await supabase.from('ga_vendors').select('*').in('id', vendorIds);
        vmap = new Map((vendors as Vendor[] ?? []).map((v) => [v.id, v]));
      }
      setReqs((data as VendorRequest[]).map((r) => ({ ...r, vendor: r.vendor_id ? vmap.get(r.vendor_id) ?? null : null })));
    })();
  }, []);

  return (
    <div>
      <SectionHeader eyebrow={`${reqs.length} requests`} title="Location requests" />
      <p className="muted" style={{ marginBottom: 24 }}>
        Members nudging us about locations they want activated. Each click here means we should call the owner.
      </p>
      {reqs.length === 0 ? (
        <p className="muted">No requests yet.</p>
      ) : (
        <div className="stack-sm">
          {reqs.map((r) => (
            <div key={r.id} style={{ padding: 20, borderRadius: 12, background: 'var(--card)' }}>
              <div className="row-between">
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 16 }}>{r.location_name}</div>
                  <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
                    {r.location_address || r.notes || '-'}
                    {r.location_neighborhood ? ` · ${r.location_neighborhood}` : ''}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--ink-soft)', marginTop: 4 }}>
                    Requested by {r.requester_email || 'anonymous'} · {relativeDate(r.created_at)}
                  </div>
                </div>
                <span className={`tag ${r.status === 'new' ? 'tag-pending' : 'tag-cancelled'}`}>{r.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Vendors() {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [filter, setFilter] = useState<VendorStatus | 'all'>('all');
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      let q = supabase.from('ga_vendors').select('*').order('created_at', { ascending: false });
      const { data } = await q;
      setVendors((data as Vendor[]) ?? []);
    })();
  }, []);

  const filtered = filter === 'all' ? vendors : vendors.filter((v) => v.status === filter);

  return (
    <div>
      <SectionHeader eyebrow={`${vendors.length} total`} title="Vendors" />
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {(['all', 'active', 'inactive', 'draft', 'paused'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f as VendorStatus | 'all')}
            className="mono"
            style={{
              padding: '8px 14px',
              borderRadius: 999,
              border: '1px solid var(--ink)',
              background: filter === f ? 'var(--ink)' : 'transparent',
              color: filter === f ? 'var(--bg)' : 'var(--ink)',
              fontSize: 12,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            {f} ({vendors.filter((v) => f === 'all' ? true : v.status === f).length})
          </button>
        ))}
      </div>
      <div className="stack-sm">
        {filtered.map((v) => (
          <div key={v.id} onClick={() => navigate(`/dashboard/vendors/${v.id}`)} style={{ padding: 20, borderRadius: 12, background: 'var(--card)', cursor: 'pointer' }}>
            <div className="row-between">
              <div>
                <div className="display" style={{ fontSize: 20 }}>{v.name}</div>
                <div style={{ fontSize: 13, color: 'var(--ink-soft)', marginTop: 2 }}>{v.slug} · {v.neighborhood} · {v.category ?? '-'}</div>
              </div>
              <span className={`tag ${v.status === 'active' ? 'tag-active' : v.status === 'inactive' ? 'tag-cancelled' : 'tag-pending'}`}>{v.status}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function VendorEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!id) return;
    (async () => {
      const { data } = await supabase.from('ga_vendors').select('*').eq('id', id).maybeSingle();
      setVendor(data as Vendor | null);
      const { data: o } = await supabase.from('ga_offers').select('*').eq('vendor_id', id);
      setOffers((o as Offer[]) ?? []);
    })();
  }, [id]);

  if (!vendor) return <p className="muted">Loading…</p>;

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!vendor) return;
    setSubmitting(true);
    const { data } = await supabase.from('ga_vendors').update({
      name: vendor.name,
      slug: vendor.slug,
      neighborhood: vendor.neighborhood,
      address: vendor.address,
      description: vendor.description,
      status: vendor.status,
    }).eq('id', vendor.id).select().maybeSingle();
    setSubmitting(false);
    if (data) setVendor(data as Vendor);
    setSaved(true);
  }

  return (
    <div>
      <SectionHeader eyebrow="Vendor" title={vendor.name} action={<button onClick={() => navigate('/dashboard/vendors')} className="btn btn-ghost btn-sm">← Back</button>} />

      <form onSubmit={save} style={{ padding: 32, borderRadius: 18, background: 'var(--card)', maxWidth: 760, marginBottom: 24 }}>
        <div className="stack">
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
            <label>
              <span className="field-label">Name</span>
              <input value={vendor.name} onChange={(e) => setVendor({ ...vendor, name: e.target.value })} />
            </label>
            <label>
              <span className="field-label">Slug</span>
              <input value={vendor.slug} onChange={(e) => setVendor({ ...vendor, slug: e.target.value })} />
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 16 }}>
            <label>
              <span className="field-label">Neighborhood</span>
              <input value={vendor.neighborhood ?? ''} onChange={(e) => setVendor({ ...vendor, neighborhood: e.target.value })} />
            </label>
            <label>
              <span className="field-label">Address</span>
              <input value={vendor.address ?? ''} onChange={(e) => setVendor({ ...vendor, address: e.target.value })} />
            </label>
            <label>
              <span className="field-label">Status</span>
              <select value={vendor.status} onChange={(e) => setVendor({ ...vendor, status: e.target.value as VendorStatus })}>
                <option value="draft">draft</option>
                <option value="active">active</option>
                <option value="paused">paused</option>
                <option value="inactive">inactive</option>
              </select>
            </label>
          </div>
          <label>
            <span className="field-label">Description</span>
            <textarea rows={4} value={vendor.description ?? ''} onChange={(e) => setVendor({ ...vendor, description: e.target.value })} />
          </label>
          {saved && <div style={{ color: 'var(--success)' }}>Saved.</div>}
          <button className="btn btn-primary" disabled={submitting} style={{ alignSelf: 'flex-start' }}>
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>

      <h3 className="display" style={{ fontSize: 22, marginBottom: 16 }}>Offers ({offers.length})</h3>
      <div className="stack-sm">
        {offers.map((o) => (
          <div key={o.id} style={{ padding: 16, borderRadius: 12, background: 'var(--card)' }}>
            <div className="row-between">
              <div>
                <div style={{ fontWeight: 500 }}>{o.title}</div>
                <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>{formatMoney(o.price_cents)}/head · {(o.days_of_week ?? []).join(', ') || o.window_days}</div>
              </div>
              <span className={`tag ${o.status === 'active' ? 'tag-active' : 'tag-pending'}`}>{o.status}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Bookings() {
  const [bookings, setBookings] = useState<(Booking & { vendor: Vendor | null; offer: Offer | null; organizer: UserProfile | null })[]>([]);

  useEffect(() => {
    (async () => {
      const { data: b } = await supabase.from('ga_bookings').select('*').order('created_at', { ascending: false }).limit(200);
      if (!b || b.length === 0) {
        setBookings([]);
        return;
      }
      const [{ data: vendors }, { data: offers }, { data: profiles }] = await Promise.all([
        supabase.from('ga_vendors').select('*').in('id', [...new Set((b as Booking[]).map((x) => x.vendor_id))]),
        supabase.from('ga_offers').select('*').in('id', [...new Set((b as Booking[]).map((x) => x.offer_id))]),
        supabase.from('ga_user_profiles').select('*').in('id', [...new Set((b as Booking[]).map((x) => x.organizer_user_id))]),
      ]);
      const vmap = new Map((vendors as Vendor[] ?? []).map((x) => [x.id, x]));
      const omap = new Map((offers as Offer[] ?? []).map((x) => [x.id, x]));
      const pmap = new Map((profiles as UserProfile[] ?? []).map((x) => [x.id, x]));
      setBookings((b as Booking[]).map((x) => ({ ...x, vendor: vmap.get(x.vendor_id) ?? null, offer: omap.get(x.offer_id) ?? null, organizer: pmap.get(x.organizer_user_id) ?? null })));
    })();
  }, []);

  return (
    <div>
      <SectionHeader eyebrow={`${bookings.length} bookings`} title="All bookings" />
      {bookings.length === 0 ? (
        <p className="muted">No bookings yet.</p>
      ) : (
        <div className="stack-sm">
          {bookings.map((b) => (
            <div key={b.id} style={{ padding: 20, borderRadius: 12, background: 'var(--card)' }}>
              <div className="row-between">
                <div>
                  <div style={{ fontWeight: 500 }}>{b.vendor?.name} · {b.offer?.title}</div>
                  <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
                    {formatDate(b.booking_date)} · {formatTime(b.booking_time)} · {b.headcount} ppl · by {b.organizer?.email}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="display" style={{ fontSize: 18 }}>{formatMoney(b.total_cents)}</div>
                  <span className={`tag ${b.status === 'confirmed' ? 'tag-active' : 'tag-pending'}`}>{b.status}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Members() {
  const [profiles, setProfiles] = useState<UserProfile[]>([]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('ga_user_profiles').select('*').order('created_at', { ascending: false }).limit(200);
      setProfiles((data as UserProfile[]) ?? []);
    })();
  }, []);

  async function promote(id: string, role: 'member' | 'platform') {
    await supabase.from('ga_user_profiles').update({ role }).eq('id', id);
    setProfiles((prev) => prev.map((p) => p.id === id ? { ...p, role } : p));
  }

  return (
    <div>
      <SectionHeader eyebrow={`${profiles.length} members`} title="Users" />
      <div className="stack-sm">
        {profiles.map((p) => (
          <div key={p.id} style={{ padding: 16, borderRadius: 12, background: 'var(--card)' }}>
            <div className="row-between">
              <div>
                <div style={{ fontWeight: 500 }}>{p.full_name || p.email}</div>
                <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>{p.email} · joined {relativeDate(p.created_at)}</div>
              </div>
              <div className="row" style={{ gap: 8 }}>
                <span className="tag">{p.role}</span>
                {/* Only hi@goaround.io can hold the platform role (DB constraint). */}
                {p.email.toLowerCase() === 'hi@goaround.io' && p.role === 'member' && (
                  <button onClick={() => promote(p.id, 'platform')} className="btn btn-ghost btn-sm">Make admin</button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionHeader({ eyebrow, title, action }: { eyebrow: string; title: string; action?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', marginBottom: 28, gap: 24, flexWrap: 'wrap' }}>
      <div>
        <div className="eyebrow" style={{ marginBottom: 12 }}>{eyebrow}</div>
        <h1 className="display" style={{ fontSize: 'clamp(32px, 4vw, 48px)', margin: 0 }}>{title}</h1>
      </div>
      {action}
    </div>
  );
}
